import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, Output, ViewChild } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { DateTimeAdapter } from '@danielmoncada/angular-datetime-picker';
import { DateHelper } from '../../../helpers/date.helper';
import { PickerMode } from '../../../models/enums/pickermode.enum';
import { StartView } from '../../../models/enums/pickerstartview.enum';
import { PickerType } from '../../../models/enums/pickertype.enum';
import { LanguageService } from '../../../services/language.service';
import { LayoutService } from '../../../services/layout.service';
import { IBaseComponent } from '../base.component';
import { PROPERTIES, PROPERTYGROUPS } from '../../../services/dynamic.component.service';
import { GenericMenuTab } from '../../../appbuilder/menutabs/generic/generic.menu.tab';
import { PropertyGroupDisplay } from '../../../models/enums/propertygroupdisplay.enum';
import { ComponentPortal } from '@angular/cdk/portal';
import { TranslationTextBoxThemeControl } from '../../../appbuilder/controls/translation/translation.textbox.theme.control';
import { ComboboxThemeControl } from '../../../appbuilder/controls/combobox/combobox.theme.control';
import { CheckBoxThemeControl } from '../../../appbuilder/controls/checkbox/checkbox.control';
import { MenuTabLabelPosition } from '../../../models/menutab/menutab.property.model';
import { BasePanel } from '../../../appbuilder/panels/base.panel';
import { DateTime } from 'luxon';

@Component({
    selector: 'evi-datetimepicker',
    templateUrl: './datetimepicker.control.html',
    styleUrls: ['./datetimepicker.control.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DateTimePickerControl extends IBaseComponent {

    static Type: any = 'datetimepicker';
    static Default = { Source: [], Type: 'datetimepicker',Layout: {
        _Editable: true,
    } };
    static Meta = {
                Type: 'datetimepicker',
                Properties: [
                    { Name: 'DataSource', Type: 'any' },
                    { Name: 'SelectedDate', Type: 'any' },
                    { Name: 'PickerType', Type: 'any' },
                    { Name: 'PickerMode', Type: 'any' },
                    { Name: 'StartView', Type: 'any' },
                    { Name: 'Placeholder', Type: 'any' },
                ],
                Events: [
                    { Name: 'datechanged' },
                ]
            };
    //#region DataSource
    DataSourceValue;

    @Input()
    get DataSource() {
        return this.DataSourceValue;
    }
    set DataSource(val) {
        this.DataSourceValue = val;
        this.DataSourceChange.emit(this.DataSourceValue);
        this.triggerEvent('DataSourceChanged', this.DataSourceValue);
        this.cdRef.detectChanges();
    }

    @Output() DataSourceChange = new EventEmitter<any>();
    //#endregion

    //#region SelectedDate
    SelectedDateValue;

    @Input()
    get SelectedDate() {
        return this.SelectedDateValue;
    }
    set SelectedDate(val) {
        this.SelectedDateValue = val;
        this.SelectedDateChange.emit(this.SelectedDateValue);
        this.cdRef.detectChanges();
    }

    @Output() SelectedDateChange = new EventEmitter<any>();
    //#endregion

    //region PickerType
    @Input()
    get PickerType() {
        if (!this.LayoutElement.PickerType) {
            return PickerType[0];
        } else {
            return PickerType[this.LayoutElement.PickerType];
        }
    }
    set PickerType(val) {
        this.LayoutElement.PickerType = val;
        this.cdRef.detectChanges();
    }
    //#endregion

    //region PickerMode
    @Input()
    get PickerMode() {
        if (!this.LayoutElement.PickerMode) {
            return PickerMode[0];
        } else {
            return PickerMode[this.LayoutElement.PickerMode];
        }

    }
    set PickerMode(val) {
        this.LayoutElement.PickerMode = val;
        this.cdRef.detectChanges();
    }
    //#endregion

    //region StartView
    @Input()
    get StartView() {
        if (!this.LayoutElement.StartView) {
            return StartView[0];
        } else {
            return StartView[this.LayoutElement.StartView];
        }

    }
    set StartView(val) {
        this.LayoutElement.StartView = val;
        this.cdRef.detectChanges();
    }
    //#endregion

    //#region Placeholder
    @Input()
    get Placeholder() {
        return this.LayoutElement.Placeholder;
    }
    set Placeholder(val) {
        this.LayoutElement.Placeholder = val;
        this.PlaceholderChange.emit(this.LayoutElement.Placeholder);
        this.cdRef.detectChanges();
    }

    @Output() PlaceholderChange = new EventEmitter<any>();
    //#endregion

    @Output() DateChanged = new EventEmitter<any>();


    constructor(translate: TranslateService, private dateTimeAdapter: DateTimeAdapter<any>, cdRef: ChangeDetectorRef, @Inject(LayoutService.CONTAINER_DATA) public data) {
        super(cdRef, data);
        this.EventList.push('datechanged');
        this.setLocale(translate.currentLang);
        translate.onLangChange.subscribe((event: LangChangeEvent) => {
            this.setLocale(event.lang);
        });
    }

    ControlInitialized() {
    }

    langinfoName;

    setLocale(lang) {
      const langinfo = LanguageService.Languages.getValue().find(
        (l) => l.LCID.toString() === lang
      );
      if (langinfo) {
        this.dateTimeAdapter.setLocale(langinfo.Name);
        this.langinfoName = langinfo.Name;
      }
    }
  
    onDateChanged(ev) {
      if (this.langinfoName) {
        // Dates formated as 01.10.2024, 10:20
        let parsedDate = DateTime.fromFormat(ev.input.value, "f", {
          locale: this.langinfoName,
        });
  
        // Dates formated as 01.10.2024
        if (!parsedDate.isValid) {
          parsedDate = DateTime.fromFormat(ev.input.value, "D", {
            locale: this.langinfoName,
          });
        }
        if (parsedDate.isValid) {
          this.DataSource = parsedDate.toJSDate();
        }
      }
  
      this.SelectedDate = this.DataSource;
  
      this.DateChanged.emit(this.SelectedDate);
      let json = "";
      if (this.SelectedDate) {
        json = DateHelper.GetSerializeString(this.SelectedDate);
      }
      this.triggerEvent("datechanged", json);
    }
  

    setFocus() {
        let control = document.getElementById(this.LayoutElement.ID);
        if (control) {
            control.focus();
        }
    }
}
export class DateTimePickerPanel extends BasePanel {
    static override SIDS = ['a01941d2-eeab-42c4-be8a-2e49cef5cf44']
    static InitPanel() {
        PROPERTYGROUPS.push({
            SID:'a01941d2-eeab-42c4-be8a-2e49cef5cf44',
            ID: 'datetimepicker',
            Caption: '@@DateTimePicker',
            Index: 100,
            Content: GenericMenuTab,
            Display: PropertyGroupDisplay.Grid,
            Columns: ['100%'],
            Rows: ['auto'],
            CheckVisibility: (item) => {
                return item.ElementType == 'datetimepicker';
            }
        });
        PROPERTIES.push({
            ID: "Placeholder",
            Parent: "datetimepicker",
            Content: new ComponentPortal(TranslationTextBoxThemeControl),
            Label: "@@Placeholder",
            InitArgs: {
                DataType: 'string'
            }
        });
        PROPERTIES.push({
            ID: "PickerType",
            Parent: "datetimepicker",
            Content: new ComponentPortal(ComboboxThemeControl),
            Label: "@@PickerType",
            InitArgs: {
                Placeholder: "@@Select",
                Multiple: false,
                EnumSource: PickerType
            }
        })
        PROPERTIES.push({
            ID: "PickerMode",
            Parent: "datetimepicker",
            Content: new ComponentPortal(ComboboxThemeControl),
            Label: "@@PickerMode",
            InitArgs: {
                Placeholder: "@@Select",
                Multiple: false,
                EnumSource: PickerMode
            }
        })
        PROPERTIES.push({
            ID: "StartView",
            Parent: "datetimepicker",
            Content: new ComponentPortal(ComboboxThemeControl),
            Label: "@@StartView",
            InitArgs: {
                Placeholder: "@@Select",
                Multiple: false,
                EnumSource: StartView
            }
        })
        PROPERTIES.push({
            ID: "FirstDayOfWeek",
            Parent: "datetimepicker",
            Content: new ComponentPortal(ComboboxThemeControl),
            Label: "@@FirstDayOfWeek",
            InitArgs: {
                Placeholder: "@@Select",
                Multiple: false,
                ItemsSource: [
                    { Caption: '@@Monday', Value: 1 },
                    { Caption: '@@Tuesday', Value: 2 },
                    { Caption: '@@Wednesday', Value: 3 },
                    { Caption: '@@Thursday', Value: 4 },
                    { Caption: '@@Friday', Value: 5 },
                    { Caption: '@@Saturday', Value: 6 },
                    { Caption: '@@Sunday', Value: 0 }
                ],
                DisplayMemberPath: 'Caption',
                ValueMemberPath:'Value'
            }
        })
        PROPERTIES.push({
            ID: "Inline",
            Parent: "datetimepicker",
            Content: new ComponentPortal(CheckBoxThemeControl),
            InitArgs: {
                Caption: "@@Inline"
            }
        });
        PROPERTIES.push({
            ID: "Hour12Timer",
            Parent: "datetimepicker",
            Content: new ComponentPortal(CheckBoxThemeControl),
            InitArgs: {
                Caption: "@@Hour12Timer"
            }
        });
        PROPERTIES.push({
            ID: "Disabled",
            Parent: "datetimepicker",
            Content: new ComponentPortal(CheckBoxThemeControl),
            InitArgs: {
                Caption: "@@Disabled"
            }
        });
    }
}